import React, { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import { IProject, IUserSummary, UserSummary } from '../../types/global/helper';
import { createFullName, formatNumberWithK } from '../../utils/AppUtils';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';

const MANROPE_ID = 'contest-submissions-manrope';

function ensureManrope() {
    if (typeof document === 'undefined' || document.getElementById(MANROPE_ID)) return;
    const link = document.createElement('link');
    link.id = MANROPE_ID;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;600;700;800&family=Inter:wght@400;500&display=swap';
    document.head.appendChild(link);
}

const PRIMARY = '#ffc63e';
const ON_SURFACE_MUTED = 'rgba(229, 226, 225, 0.5)';

function withAlphaPrimary(alpha: number) {
    return `rgba(255,198,62,${alpha})`;
}

function formatDuration(seconds?: number) {
    if (seconds == null || Number.isNaN(seconds)) return null;
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

interface Props {
    project: IProject;
    user: UserSummary | IUserSummary;
    totalVotes?: number;
    onPlay: () => void;
}

export const CinematicEntryCard: React.FC<Props> = ({ project, user, totalVotes = 0, onPlay }) => {
    useEffect(() => {
        ensureManrope();
    }, []);

    const thumb = project.thumbnail || project.feature_image;
    const duration = formatDuration(project.metadata?.duration);
    const views = project.view_count ?? 0;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '100%',
                cursor: 'pointer',
                '&:hover .entry-title': { color: `${PRIMARY} !important` },
            }}
            onClick={onPlay}>
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '16 / 9',
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    boxShadow: '0 20px 40px -12px rgba(0,0,0,0.5)',
                    outline: '1px solid rgba(77, 70, 53, 0.1)',
                    '&:hover .cine-thumb': { transform: 'scale(1.1)' },
                    '&:hover .cine-dim': { bgcolor: 'rgba(0,0,0,0)' },
                    '&:hover .cine-play': { opacity: 1 },
                }}>
                <Box
                    className="cine-thumb"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        transition: 'transform 0.5s ease',
                    }}>
                    {thumb ? (
                        <CustomLazyLoadImage
                            src={thumb}
                            alt=""
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    ) : (
                        <Box sx={{ width: '100%', height: '100%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                    )}
                </Box>
                <Box
                    className="cine-dim"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        bgcolor: 'rgba(0,0,0,0.2)',
                        transition: 'background-color 0.35s ease',
                        pointerEvents: 'none',
                    }}
                />
                <Box
                    className="cine-play"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.35s ease',
                        pointerEvents: 'none',
                    }}>
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            bgcolor: withAlphaPrimary(0.92),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#402d00',
                            boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
                        }}>
                        <PlayArrowIcon sx={{ fontSize: 40, ml: 0.5 }} />
                    </Box>
                </Box>
                {duration && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 12,
                            left: 12,
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            backdropFilter: 'blur(8px)',
                        }}>
                        <Typography
                            sx={{
                                fontFamily: 'Inter, system-ui, sans-serif',
                                fontSize: 10,
                                fontWeight: 700,
                                color: '#fff',
                            }}>
                            {duration}
                        </Typography>
                    </Box>
                )}
            </Box>
            <Box sx={{ px: 1 }}>
                <Typography
                    className="entry-title"
                    sx={{
                        fontFamily: '"Manrope", system-ui, sans-serif',
                        fontWeight: 700,
                        fontSize: '1.125rem',
                        lineHeight: 1.35,
                        color: 'rgba(229, 226, 225, 0.92)',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        transition: 'color 0.25s ease',
                    }}>
                    {project.title}
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 1.5,
                        flexWrap: 'wrap',
                        gap: 1,
                    }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {(user as UserSummary)?.profile_picture ? (
                            <Box
                                component="img"
                                src={(user as UserSummary).profile_picture}
                                alt=""
                                sx={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(255,255,255,0.12)',
                                }}
                            />
                        )}
                        <Typography
                            sx={{
                                fontFamily: 'Inter, system-ui, sans-serif',
                                fontSize: 12,
                                color: ON_SURFACE_MUTED,
                            }}>
                            {createFullName(user as UserSummary)}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: ON_SURFACE_MUTED }}>
                            <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12 }}>
                                {formatNumberWithK(views)}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: ON_SURFACE_MUTED }}>
                            <ThumbUpOutlinedIcon sx={{ fontSize: 16 }} />
                            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12 }}>
                                {formatNumberWithK(totalVotes)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
